import { computed, defineComponent, reactive, ref } from 'vue';
import { useSweetAlert } from '@/composables/sweetalert';
import { useStore } from 'vuex';
import { DownloadAction, DownloadGetter } from '@/store/download/download.module';
import { DownloadSchema } from '@/store/download/download.validator';
import { Download } from '@/store/download/download.model';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'DownloadFormComponent',
  components: {
    BaseModalComponent
  },
  props: {
    downloadId: {
      type: Number
    },
  },
  setup: (props) => {
    const store = useStore();
    const swal = useSweetAlert();
    const fileDialog = ref();
    const isEditMode = computed(() => !!props.downloadId);
    const download = computed<Download | undefined>(() => store.getters[DownloadGetter.BY_ID](props.downloadId));
    const title = computed(() => isEditMode.value ? 'แก้ไขเอกสารดาวน์โหลด' : 'เพิ่มเอกสารดาวน์โหลด');
    const downloadForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      id?: number | null;
      title?: string | null;
      pined?: boolean;
      file?: string | null;
    }
    const formData = reactive<Form>({
      id: null,
      title: '',
      pined: false,
      file: '',
    });
    const validator = computed(() => DownloadSchema.validate(formData, { abortEarly: false }).error);
    const errors = reactive({
      title: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'title')),
      file: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'file')),
    });
    const isValid = computed(() => !errors.title && !errors.file);
    const isLoading = computed(() => store.getters[DownloadGetter.STATUS] === 'init' || store.getters[DownloadGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.id = (isEditMode.value) ? props.downloadId : null;
        formData.title = download.value?.title;
        formData.pined = (isEditMode.value) ? download.value?.pined : false;
        formData.file = download.value?.file;
        resolve();
      }).then(() => downloadForm.value?.show());
    };

    // hide modal
    const hide = () => {
      downloadForm.value?.hide();
    };

    // change file
    const fileChange = (event: Event) => {
      const files = (event.target as HTMLInputElement).files;
      const file = files?.item(0);

      if (file) {
        formData.file = file.name;
      }
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.title = '';
        formData.pined = false;
        formData.file = '';
        fileDialog.value.value = null;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(DownloadAction.UPDATE, {
            id: formData.id,
            title: formData.title,
            pined: formData.pined,
            file: (fileDialog.value?.files?.length !== 0) ? fileDialog.value?.files?.item(0) as File : null,
          }).then(() => hide());
        } else {
          store.dispatch(DownloadAction.CREATE, {
            title: formData.title,
            pined: formData.pined,
            file: fileDialog.value?.files?.item(0) as File,
          }).then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(DownloadAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      downloadForm,
      isEditMode,
      fileDialog,
      errors,
      submited,
      isLoading,
      show,
      hide,
      fileChange,
      unsubmit,
      clearForm,
      submitClick,
      deleteClick,
    };
  },
});
